'use client';

import { c as _c } from "react/compiler-runtime";
import { useObservable, useObserveEffect } from '@legendapp/state/react';
import { type ReactNode, createContext, use, useEffect } from 'react';
import type { Observable } from '@legendapp/state';
import { useBreakpointDown$ } from '@/hooks/useBreakpointDown$';
import { type Region, type Site, findSiteInRegion } from '@/services/rental-api-client';
export type LocationSelectState = {
  regions: Region[];
  pickUpLocation?: Site;
  dropOffLocation?: Site;
  selecting?: 'pickUp' | 'dropOff';
  setActiveValue: (value: Site) => void;
  variant?: 'horizontal' | 'vertical';
};
const LocationSelectContext = createContext<Observable<LocationSelectState>>(undefined as unknown as Observable<LocationSelectState>);
export type LocationSelectProviderProps = {
  regions: Region[];
  pickUpLocationCode?: string;
  dropOffLocationCode?: string;
  variant?: LocationSelectState['variant'];
  children: ReactNode;
  onRegionChange?: (region: string) => void;
};
export function LocationSelectProvider(t0) {
  const $ = _c(30);
  const {
    children,
    regions,
    variant,
    pickUpLocationCode,
    dropOffLocationCode,
    onRegionChange
  } = t0;
  let t1;
  if ($[0] !== pickUpLocationCode || $[1] !== regions) {
    t1 = pickUpLocationCode ? findSiteInRegion(regions, pickUpLocationCode) : undefined;
    $[0] = pickUpLocationCode;
    $[1] = regions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== dropOffLocationCode || $[4] !== regions) {
    t2 = dropOffLocationCode ? findSiteInRegion(regions, dropOffLocationCode) : undefined;
    $[3] = dropOffLocationCode;
    $[4] = regions;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const state$ = useObservable({
    regions,
    pickUpLocation: t1,
    dropOffLocation: t2,
    setActiveValue: value => {
      if (state$.selecting.get() === "pickUp") {
        state$.pickUpLocation.set(value);
      } else {
        state$.dropOffLocation.set(value);
      }
    },
    variant: useResolveVariant(variant) as unknown as LocationSelectState['variant']
  });
  let t3;
  if ($[6] !== onRegionChange || $[7] !== state$.dropOffLocation || $[8] !== state$.pickUpLocation || $[9] !== state$.selecting) {
    t3 = () => {
      const pickUp = state$.pickUpLocation.get();
      const dropOff = state$.dropOffLocation.get();
      if (!pickUp || !dropOff) {
        return;
      }
      if (pickUp.countryCode === dropOff.countryCode) {
        return;
      }
      if (pickUp.countryCode !== dropOff.countryCode) {
        onRegionChange?.(pickUp.countryCode);
      }
      state$.dropOffLocation.delete();
      state$.selecting.set("dropOff");
    };
    $[6] = onRegionChange;
    $[7] = state$.dropOffLocation;
    $[8] = state$.pickUpLocation;
    $[9] = state$.selecting;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useObserveEffect(t3);
  let t4;
  if ($[11] !== pickUpLocationCode || $[12] !== regions || $[13] !== state$.pickUpLocation) {
    t4 = () => {
      if (!pickUpLocationCode) {
        state$.pickUpLocation.delete();
        return;
      }
      state$.pickUpLocation.set(findSiteInRegion(regions, pickUpLocationCode));
    };
    $[11] = pickUpLocationCode;
    $[12] = regions;
    $[13] = state$.pickUpLocation;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== pickUpLocationCode || $[16] !== regions || $[17] !== state$) {
    t5 = [pickUpLocationCode, regions, state$];
    $[15] = pickUpLocationCode;
    $[16] = regions;
    $[17] = state$;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useEffect(t4, t5);
  let t6;
  if ($[19] !== dropOffLocationCode || $[20] !== regions || $[21] !== state$.dropOffLocation) {
    t6 = () => {
      if (!dropOffLocationCode) {
        state$.dropOffLocation.delete();
        return;
      }
      state$.dropOffLocation.set(findSiteInRegion(regions, dropOffLocationCode));
    };
    $[19] = dropOffLocationCode;
    $[20] = regions;
    $[21] = state$.dropOffLocation;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== dropOffLocationCode || $[24] !== regions || $[25] !== state$) {
    t7 = [dropOffLocationCode, regions, state$];
    $[23] = dropOffLocationCode;
    $[24] = regions;
    $[25] = state$;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  useEffect(t6, t7);
  let t8;
  if ($[27] !== children || $[28] !== state$) {
    t8 = <LocationSelectContext value={state$}>{children}</LocationSelectContext>;
    $[27] = children;
    $[28] = state$;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
export function useResolveVariant(variant) {
  const $ = _c(4);
  const isSm$ = useBreakpointDown$("lg");
  const result = useObservable(variant);
  let t0;
  if ($[0] !== isSm$ || $[1] !== result || $[2] !== variant) {
    t0 = () => {
      const isSm = isSm$.get();
      if (variant !== undefined) {
        result.set(variant);
        return;
      }
      result.set(isSm ? "vertical" : "horizontal");
    };
    $[0] = isSm$;
    $[1] = result;
    $[2] = variant;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  useObserveEffect(t0);
  return result;
}
export function useLocationSelectState() {
  const context = use(LocationSelectContext);
  if (!context) {
    throw new Error('useLocationSelectState must be used within a LocationSelectProvider');
  }
  return context;
}